import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Search, Home, Building2, UserCheck, CheckCircle2, AlertCircle } from "lucide-react";
import { ServicesSection } from "./ServicesSection";
import { proximaAccionOptions, API_URL } from "./constants";
import { validateDocument, validateIban, validateEmail, validatePhone } from "../../utils/documentValidation";
import { COUNTRY_DIRECTORY, formatCountryLabel, detectCountryFromPhone } from "../../utils/countries";

/**
 * Renders the small validation hint below an input.
 * Empty input → no message (avoids false-positive red on optional fields).
 * Valid → subtle green check + helper text.
 * Invalid → subtle red helper text + alert icon.
 */
const ValidationHint = ({ result, hideWhenValid = false }) => {
  if (!result || !result.normalized) return null;
  if (result.valid) {
    if (hideWhenValid) return null;
    return (
      <div className="flex items-center gap-1 mt-1 text-xs text-emerald-400" data-testid="validation-hint-ok">
        <CheckCircle2 className="h-3 w-3" />
        <span>Válido</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 mt-1 text-xs text-rose-400" data-testid="validation-hint-error">
      <AlertCircle className="h-3 w-3" />
      <span>{result.error}</span>
    </div>
  );
};

export const CreateOpportunityDialog = ({
  open,
  onOpenChange,
  createMode,
  setCreateMode,
  customerSearch,
  setCustomerSearch,
  showCustomerDropdown,
  setShowCustomerDropdown,
  filteredCustomers,
  onSelectCustomer,
  newCustomer,
  setNewCustomer,
  newOpp,
  setNewOpp,
  catalogServices,
  users,
  formatCurrency,
  onCreate,
  // Legacy props accepted but unused after FIX 1
  ...rest
}) => {
  // Auto-generated title concatenates selected service names. Use " / " (not " + ")
  // because many product names already contain "+" internally (e.g.
  // "Móvil 25GB + Ilimitadas"), which made the joined string ambiguous.
  const autoTitle = newOpp.selected_services?.map(s => s.nombre).join(" / ") || "";
  const [responsableNombre, setResponsableNombre] = useState(null);
  const [rulesValidation, setRulesValidation] = useState({ ok: true, violations: [] });

  // Inline validation results for the customer document + IBAN inputs.
  // Recomputed on every keystroke against the SAME rules the backend
  // enforces (shared `documentValidation` helper). They never block the
  // form — submission lets the backend speak the final word — but they
  // give immediate red/green feedback while typing.
  const docValidation = useMemo(
    () => validateDocument(
      newCustomer?.customer_type,
      newCustomer?.document_type,
      newCustomer?.customer_type === "EMPRESA" ? newCustomer?.cif : newCustomer?.dni_nie,
    ),
    [newCustomer?.customer_type, newCustomer?.document_type, newCustomer?.cif, newCustomer?.dni_nie],
  );
  const ibanValidation = useMemo(
    () => validateIban(newCustomer?.iban),
    [newCustomer?.iban],
  );
  // Representative document (empresa) — uses personal-doc rules.
  const repDocValidation = useMemo(
    () => validateDocument("RESIDENCIAL", newCustomer?.representante_document_type, newCustomer?.dni_representante),
    [newCustomer?.representante_document_type, newCustomer?.dni_representante],
  );
  const emailValidation = useMemo(() => validateEmail(newCustomer?.email), [newCustomer?.email]);
  const phoneValidation = useMemo(
    () => validatePhone(newCustomer?.phone, newCustomer?.phone_country || "ES"),
    [newCustomer?.phone, newCustomer?.phone_country],
  );
  const inputBorderClass = (result) => {
    if (!result || !result.normalized) return "border-[#1E1E5F]";
    return result.valid ? "border-emerald-500/60" : "border-rose-500/70";
  };

  // Phone country auto-detection: if the user types a phone that starts
  // with a known international dial code (+34, +33, +376, ...) we update
  // `phone_country` automatically so the validation rule follows. The
  // user can still manually override via the country dropdown afterwards.
  const handlePhoneChange = (raw) => {
    const detected = detectCountryFromPhone(raw);
    setNewCustomer(prev => ({
      ...prev,
      phone: raw,
      phone_country: detected || prev.phone_country || "ES",
    }));
  };

  // Fetch responsable when ejecutivo changes
  useEffect(() => {
    if (!newOpp.ejecutivo_id) {
      setResponsableNombre(null);
      return;
    }
    axios.get(`${API_URL}/api/users/${newOpp.ejecutivo_id}`)
      .then(res => setResponsableNombre(res.data.responsable_nombre || null))
      .catch(() => setResponsableNombre(null));
  }, [newOpp.ejecutivo_id]);

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#0F0F3D] border-[#1E1E5F] text-white max-w-6xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-xl text-white">Nueva Oportunidad</DialogTitle>
      </DialogHeader>
      
      {/* Customer Selection */}
      <div className="mt-4 space-y-4">
        <div className="flex gap-2">
          <Button
            variant={createMode === "existing" ? "default" : "outline"}
            onClick={() => setCreateMode("existing")}
            className={createMode === "existing" ? "bg-[#42E695] text-[#04042C]" : "border-[#1E1E5F] text-slate-300"}
            size="sm"
          >
            Cliente Existente
          </Button>
          <Button
            variant={createMode === "new" ? "default" : "outline"}
            onClick={() => setCreateMode("new")}
            className={createMode === "new" ? "bg-[#42E695] text-[#04042C]" : "border-[#1E1E5F] text-slate-300"}
            size="sm"
          >
            Nuevo Cliente
          </Button>
        </div>
        
        {/* Existing Customer */}
        {createMode === "existing" && (
          <div className="relative">
            <Label className="text-slate-300">Buscar Cliente *</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Escribe nombre, ID o email del cliente..."
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  setShowCustomerDropdown(true);
                  if (!e.target.value) {
                    setNewOpp(prev => ({ ...prev, customer_id: "", customer_name: "" }));
                  }
                }}
                onFocus={() => setShowCustomerDropdown(true)}
                className="pl-10 bg-[#0A0A35] border-[#1E1E5F] text-white"
                data-testid="customer-search-input"
              />
            </div>
            {showCustomerDropdown && filteredCustomers.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-[#0A0A35] border border-[#1E1E5F] rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    onClick={() => onSelectCustomer(customer)}
                    className="px-4 py-3 hover:bg-[#1B1B4B] cursor-pointer border-b border-[#1E1E5F] last:border-0"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-[#42E695] font-mono text-sm mr-2">{customer.customer_id}</span>
                        <span className="text-white">
                          {customer.razon_social || `${customer.nombre || ""} ${customer.apellidos || ""}`.trim()}
                        </span>
                      </div>
                      <Badge className="bg-[#1B1B4B] text-slate-300 text-xs">
                        {customer.customer_type}
                      </Badge>
                    </div>
                    {customer.email && <p className="text-slate-400 text-sm mt-1">{customer.email}</p>}
                  </div>
                ))}
              </div>
            )}
            {newOpp.customer_id && (
              <p className="text-sm text-[#42E695] mt-2">Cliente seleccionado: {newOpp.customer_name}</p>
            )}
          </div>
        )}
        
        {/* New Customer */}
        {createMode === "new" && (
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Tipo de Cliente</Label>
              <Select value={newCustomer.customer_type} onValueChange={(v) => setNewCustomer({ ...newCustomer, customer_type: v })}>
                <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                  <SelectItem value="RESIDENCIAL" className="text-white"><Home className="h-4 w-4 inline mr-2" />Residencial</SelectItem>
                  <SelectItem value="EMPRESA" className="text-white"><Building2 className="h-4 w-4 inline mr-2" />Empresa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newCustomer.customer_type === "RESIDENCIAL" ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-slate-300">Nombre *</Label><Input value={newCustomer.nombre} onChange={(e) => setNewCustomer({ ...newCustomer, nombre: e.target.value })} className="bg-[#0A0A35] border-[#1E1E5F] text-white" /></div>
                  <div><Label className="text-slate-300">Apellidos *</Label><Input value={newCustomer.apellidos} onChange={(e) => setNewCustomer({ ...newCustomer, apellidos: e.target.value })} className="bg-[#0A0A35] border-[#1E1E5F] text-white" /></div>
                  <div className="col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-slate-300">Tipo de Documento</Label>
                      <Select
                        value={newCustomer.document_type || "DNI"}
                        onValueChange={(v) => setNewCustomer({ ...newCustomer, document_type: v })}
                      >
                        <SelectTrigger data-testid="customer-document-type-select" className="bg-[#0A0A35] border-[#1E1E5F] text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                          <SelectItem value="DNI" className="text-white">DNI</SelectItem>
                          <SelectItem value="NIE" className="text-white">NIE</SelectItem>
                          <SelectItem value="PASAPORTE" className="text-white">Pasaporte</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-slate-300">{newCustomer.document_type === "NIE" ? "NIE" : newCustomer.document_type === "PASAPORTE" ? "Pasaporte" : "DNI"}</Label>
                      <Input
                        data-testid="customer-dni-input"
                        value={newCustomer.dni_nie}
                        onChange={(e) => setNewCustomer({ ...newCustomer, dni_nie: e.target.value })}
                        className={`bg-[#0A0A35] text-white ${inputBorderClass(docValidation)}`}
                      />
                      <ValidationHint result={docValidation} />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Email</Label>
                    <Input
                      type="email"
                      data-testid="customer-email-input"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                      className={`bg-[#0A0A35] text-white ${inputBorderClass(emailValidation)}`}
                    />
                    <ValidationHint result={emailValidation} />
                  </div>
                  <div>
                    <Label className="text-slate-300">Teléfono</Label>
                    <div className="flex gap-2">
                      <Select
                        value={newCustomer.phone_country || "ES"}
                        onValueChange={(v) => setNewCustomer({ ...newCustomer, phone_country: v })}
                      >
                        <SelectTrigger data-testid="customer-phone-country-select" className="w-44 bg-[#0A0A35] border-[#1E1E5F] text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-72">
                          {COUNTRY_DIRECTORY.map(c => (
                            <SelectItem key={c.iso} value={c.iso} className="text-white">{formatCountryLabel(c)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        data-testid="customer-phone-input"
                        value={newCustomer.phone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        placeholder="612 345 678 o +34 612 345 678"
                        className={`flex-1 bg-[#0A0A35] text-white ${inputBorderClass(phoneValidation)}`}
                      />
                    </div>
                    <ValidationHint result={phoneValidation} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-300">IBAN</Label>
                    <Input
                      data-testid="customer-iban-input"
                      value={newCustomer.iban}
                      onChange={(e) => setNewCustomer({ ...newCustomer, iban: e.target.value })}
                      placeholder="ES00 0000 0000 0000 0000 0000"
                      className={`bg-[#0A0A35] text-white ${inputBorderClass(ibanValidation)}`}
                    />
                    <ValidationHint result={ibanValidation} />
                  </div>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-slate-300">Razón Social *</Label><Input value={newCustomer.razon_social} onChange={(e) => setNewCustomer({ ...newCustomer, razon_social: e.target.value })} className="bg-[#0A0A35] border-[#1E1E5F] text-white" /></div>
                <div>
                  <Label className="text-slate-300">CIF/NIF *</Label>
                  <Input
                    data-testid="customer-cif-input"
                    value={newCustomer.cif}
                    onChange={(e) => setNewCustomer({ ...newCustomer, cif: e.target.value })}
                    className={`bg-[#0A0A35] text-white ${inputBorderClass(docValidation)}`}
                  />
                  <ValidationHint result={docValidation} />
                </div>
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label className="text-slate-300">Nombre del representante</Label><Input data-testid="customer-representante-nombre-input" value={newCustomer.representante_nombre || ""} onChange={(e) => setNewCustomer({ ...newCustomer, representante_nombre: e.target.value })} className="bg-[#0A0A35] border-[#1E1E5F] text-white" /></div>
                  <div>
                    <Label className="text-slate-300">Tipo doc. representante</Label>
                    <Select
                      value={newCustomer.representante_document_type || "DNI"}
                      onValueChange={(v) => setNewCustomer({ ...newCustomer, representante_document_type: v })}
                    >
                      <SelectTrigger data-testid="rep-document-type-select" className="bg-[#0A0A35] border-[#1E1E5F] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                        <SelectItem value="DNI" className="text-white">DNI</SelectItem>
                        <SelectItem value="NIE" className="text-white">NIE</SelectItem>
                        <SelectItem value="PASAPORTE" className="text-white">Pasaporte</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300">{newCustomer.representante_document_type === "NIE" ? "NIE" : newCustomer.representante_document_type === "PASAPORTE" ? "Pasaporte" : "DNI"} del representante</Label>
                    <Input
                      data-testid="customer-dni-representante-input"
                      value={newCustomer.dni_representante || ""}
                      onChange={(e) => setNewCustomer({ ...newCustomer, dni_representante: e.target.value })}
                      className={`bg-[#0A0A35] text-white ${inputBorderClass(repDocValidation)}`}
                    />
                    <ValidationHint result={repDocValidation} />
                  </div>
                </div>
                <div><Label className="text-slate-300">Persona de Contacto</Label><Input value={newCustomer.persona_contacto} onChange={(e) => setNewCustomer({ ...newCustomer, persona_contacto: e.target.value })} className="bg-[#0A0A35] border-[#1E1E5F] text-white" /></div>
                <div>
                  <Label className="text-slate-300">Teléfono</Label>
                  <div className="flex gap-2">
                    <Select
                      value={newCustomer.phone_country || "ES"}
                      onValueChange={(v) => setNewCustomer({ ...newCustomer, phone_country: v })}
                    >
                      <SelectTrigger data-testid="customer-phone-country-select-empresa" className="w-44 bg-[#0A0A35] border-[#1E1E5F] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-72">
                        {COUNTRY_DIRECTORY.map(c => (
                          <SelectItem key={c.iso} value={c.iso} className="text-white">{formatCountryLabel(c)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      data-testid="customer-phone-input-empresa"
                      value={newCustomer.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      placeholder="912 345 678 o +34 912 345 678"
                      className={`flex-1 bg-[#0A0A35] text-white ${inputBorderClass(phoneValidation)}`}
                    />
                  </div>
                  <ValidationHint result={phoneValidation} />
                </div>
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    data-testid="customer-email-input-empresa"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    className={`bg-[#0A0A35] text-white ${inputBorderClass(emailValidation)}`}
                  />
                  <ValidationHint result={emailValidation} />
                </div>
                <div>
                  <Label className="text-slate-300">IBAN</Label>
                  <Input
                    data-testid="customer-iban-input-empresa"
                    value={newCustomer.iban}
                    onChange={(e) => setNewCustomer({ ...newCustomer, iban: e.target.value })}
                    placeholder="ES00 0000 0000 0000 0000 0000"
                    className={`bg-[#0A0A35] text-white ${inputBorderClass(ibanValidation)}`}
                  />
                  <ValidationHint result={ibanValidation} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Services Section — PackageBuilder only, no tabs */}
      <ServicesSection
        newOpp={newOpp}
        setNewOpp={setNewOpp}
        catalogServices={catalogServices}
        formatCurrency={formatCurrency}
        onValidationChange={setRulesValidation}
      />
      
      {/* Opportunity Details */}
      <div className="space-y-4 pt-4 border-t border-[#1E1E5F] mt-4">
        <h3 className="text-lg font-semibold text-[#03BEE4]">Datos de la Oportunidad</h3>
        <div>
          <Label className="text-slate-300">Título (se genera automáticamente si lo dejas vacío)</Label>
          <Input
            value={newOpp.title || autoTitle}
            onChange={(e) => setNewOpp(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Ej: Fibra 1Gbps + VoIP"
            className="bg-[#0A0A35] border-[#1E1E5F] text-white"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300">Ejecutivo de Ventas *</Label>
            <Select value={newOpp.ejecutivo_id} onValueChange={(v) => setNewOpp(prev => ({ ...prev, ejecutivo_id: v }))}>
              <SelectTrigger data-testid="opp-ejecutivo-select" className="bg-[#0A0A35] border-[#1E1E5F] text-white"><SelectValue placeholder="Asignar ejecutivo..." /></SelectTrigger>
              <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                {users.map((user) => (<SelectItem key={user.id} value={user.id} className="text-white">{user.name}</SelectItem>))}
              </SelectContent>
            </Select>
            {newOpp.ejecutivo_id && (
              <div className="flex items-center gap-1.5 mt-2" data-testid="responsable-info-create">
                <UserCheck className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-xs text-slate-400">Responsable:</span>
                <span className="text-xs text-white">{responsableNombre || "Sin responsable"}</span>
              </div>
            )}
          </div>
          <div>
            <Label className="text-slate-300">Próxima Acción</Label>
            <Select value={newOpp.proxima_accion} onValueChange={(v) => setNewOpp(prev => ({ ...prev, proxima_accion: v }))}>
              <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white"><SelectValue placeholder="Seleccionar acción..." /></SelectTrigger>
              <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                {proximaAccionOptions.map((a) => (<SelectItem key={a} value={a} className="text-white">{a}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-slate-300">Fecha Estimada de Cierre</Label>
          <Input type="date" value={newOpp.fecha_estimada_cierre} onChange={(e) => setNewOpp(prev => ({ ...prev, fecha_estimada_cierre: e.target.value }))} className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white" />
        </div>
        <div>
          <Label className="text-slate-300">Notas</Label>
          <Textarea value={newOpp.notes} onChange={(e) => setNewOpp(prev => ({ ...prev, notes: e.target.value }))} rows={2} className="bg-[#0A0A35] border-[#1E1E5F] text-white" />
        </div>
      </div>
      
      <div className="flex flex-col items-end gap-2 pt-4">
        {!rulesValidation.ok && (
          <p className="text-xs text-red-300" data-testid="rules-validation-save-hint">
            No se puede crear la oportunidad: revisa las reglas de negocio.
          </p>
        )}
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#1E1E5F] text-slate-300">Cancelar</Button>
          <Button
            onClick={() => { if (!rulesValidation.ok) return; onCreate(); }}
            disabled={!rulesValidation.ok}
            aria-disabled={!rulesValidation.ok}
            className={`bg-[#42E695] text-[#04042C] ${!rulesValidation.ok ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="create-opportunity-btn"
          >
            Crear Oportunidad
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
  );
};
